import * as React from "react";

// Third-party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// Types
import type { PropertyResponse } from "@/types/finance";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import { PencilLine, ArrowLeft } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Services
import { getProperty } from "@/services/finance";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/finance-property/"
)({
  component: FinanceProperty,
});

function FinanceProperty() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId"); // Get companyId from local storage
  const yearly = Number(localStorage.getItem("year")); // Get year from local storage
  const { company_financial_report } = useAccessPermission();
  const {
    data: propertyData,
    isLoading: propertyLoading,
    isError: propertyError,
  } = useQuery<PropertyResponse>({
    queryKey: ["property", companyId, yearly],
    queryFn: () => getProperty(companyId as string, yearly),
  });

  const { data: lastpropertyData, isLoading: lastpropertyLoading } =
    useQuery<PropertyResponse>({
      queryKey: ["propertylast", companyId, yearly],
      queryFn: () => getProperty(companyId as string, yearly - 1),
    });

  React.useEffect(() => {
    console.log(propertyData, "Property");

    if (!propertyData?.id && !propertyLoading) {
      console.log("True");

      navigate({ to: "/report/finance/company/finance-property/add" });
    }
  }, [propertyData, navigate, propertyLoading]);

  const [unit, setUnit] = React.useState<string>("1");

  if (propertyError) return <div>Error: Failed to fetch property data.</div>;
  // if (fixAssetsError)
  // 	return <div>Error: Failed to fetch fixed assets data.</div>;

  // Asset Processing
  const revolvingAssets =
    propertyData?.assetDetails?.filter(
      (asset) => asset.assetType === "REVOLVING"
    ) || [];
  const nonRevolvingAssets =
    propertyData?.assetDetails?.filter(
      (asset) => asset.assetType === "NON_REVOLVING"
    ) || [];

  const REVOLVING_Total_amount = revolvingAssets.reduce(
    (total, asset) => total + asset.amount,
    0
  );
  const NON_REVOLVING_Total_amount = nonRevolvingAssets.reduce(
    (total, asset) => total + asset.amount,
    0
  );
  //Asset last year
  const lastrevolvingAssets =
    lastpropertyData?.assetDetails?.filter(
      (asset) => asset.assetType === "REVOLVING"
    ) || [];
  const lastnonRevolvingAssets =
    lastpropertyData?.assetDetails?.filter(
      (asset) => asset.assetType === "NON_REVOLVING"
    ) || [];

  const lastREVOLVING_Total_amount = lastrevolvingAssets.reduce(
    (total, asset) => total + asset.amount,
    0
  );
  const lastNON_REVOLVING_Total_amount = lastnonRevolvingAssets.reduce(
    (total, asset) => total + asset.amount,
    0
  );

  const formatDisplayNumber = (value: number): string => {
    if (!value && value !== 0) return "-";

    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const unitNumber = parseFloat(unit);
    const formattedValue = (absValue / unitNumber).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-[4px]  mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {yearly}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>
          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/finance-property/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 gap-x-1 w-[120px] p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-3">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          {/* Revolving and Non-Revolving Assets Tables */}
          {propertyLoading || lastpropertyLoading ? (
            <DinamixSkeletonTable rows={5} columns={3} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className=" [&>*]:border [&>*]:font-bold [&>*]:text-black [&>*]:whitespace-nowrap">
                  <TableHead>ຊັບສິນ</TableHead>
                  <TableHead>ມູນຄ່າຍັງເຫຼືອປີນີ້ {yearly}</TableHead>
                  <TableHead>ມູນຄ່າຍັງເຫຼືອປີທີ່ຜ່ານມາ {yearly - 1}</TableHead>
                  <TableHead>ໝາຍເຫດ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Revolving Assets */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>ຊັບສິນໝູນວຽນ I</TableCell>
                </TableRow>
                {revolvingAssets?.map((asset, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell>{asset.name}</TableCell>
                    <TableCell className="text-right">
                      {formatDisplayNumber(asset.amount / parseFloat(unit))}
                    </TableCell>
                    <TableCell className="text-right">
                      {lastrevolvingAssets?.find(
                        (lastasset) => lastasset.name === asset.name
                      )?.amount
                        ? formatDisplayNumber(
                            lastrevolvingAssets.find(
                              (lastasset) => lastasset.name === asset.name
                            )!.amount
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>{asset.remark || "-"}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-gray-100 [&>*]:border [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ລວມ I</TableCell>
                  <TableCell className="text-right">
                    {formatDisplayNumber(
                      REVOLVING_Total_amount / parseFloat(unit)
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDisplayNumber(
                      lastREVOLVING_Total_amount / parseFloat(unit)
                    )}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
                {/* Non-Revolving Assets */}
                <TableRow>
                  <TableCell colSpan={3}> </TableCell>
                </TableRow>
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>ຊັບສິນບໍ່ໝູນວຽນ II</TableCell>
                </TableRow>
                {nonRevolvingAssets?.map((asset, index) => (
                  <TableRow key={index} className=" [&>*]:border ">
                    <TableCell>{asset.name}</TableCell>
                    <TableCell className="text-right">
                      {formatDisplayNumber(asset.amount / parseFloat(unit))}
                    </TableCell>
                    <TableCell className="text-right">
                      {lastnonRevolvingAssets?.find(
                        (lastasset) => lastasset.name === asset.name
                      )?.amount
                        ? formatDisplayNumber(
                            lastnonRevolvingAssets.find(
                              (lastasset) => lastasset.name === asset.name
                            )!.amount / parseFloat(unit)
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>{asset.remark || "-"}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-gray-100 [&>*]:border [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ລວມ II</TableCell>
                  <TableCell className="text-right">
                    {formatDisplayNumber(
                      NON_REVOLVING_Total_amount / parseFloat(unit)
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDisplayNumber(
                      lastNON_REVOLVING_Total_amount / parseFloat(unit)
                    )}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
                {/* Total Assets */}
                <TableRow className="bg-gray-100 [&>*]:border [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ລວມຍອດ I+II</TableCell>
                  <TableCell className="text-right">
                    {formatDisplayNumber(
                      (REVOLVING_Total_amount + NON_REVOLVING_Total_amount) /
                        parseFloat(unit)
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDisplayNumber(
                      (lastREVOLVING_Total_amount +
                        lastNON_REVOLVING_Total_amount) /
                        parseFloat(unit)
                    )}
                  </TableCell>
                  <TableCell>-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}

          {propertyData?.docFile
            ? company_financial_report.isDownload && (
                <FileHandleViewer fileUrl={propertyData?.docFile} />
              )
            : null}
        </CardContent>
      </Card>
    </section>
  );
}
